import { AlertTriangle } from "lucide-react";

const TONE = { amber: "border-amber-900/50 bg-amber-500/[0.04] text-amber-200", sky: "border-sky-900/50 bg-sky-500/[0.04] text-sky-200", emerald: "border-emerald-900/50 bg-emerald-500/[0.04] text-emerald-200" };
const TIER_CLS = { insufficient: "text-slate-400 border-slate-700", reportable: "text-amber-200 border-amber-800", meaningful: "text-emerald-200 border-emerald-800", not_meaningful: "text-rose-200 border-rose-800" };
const ci = (c) => (c ? `${c[0]}–${c[1]}` : "—");
const signed = (v, d = 3) => (v == null ? "—" : `${v > 0 ? "+" : ""}${Number(v).toFixed(d)}%`);

export const Tier = ({ t }) => <span className={`text-[10px] uppercase tracking-wider rounded-full border px-2 py-0.5 ${TIER_CLS[t] || TIER_CLS.insufficient}`} data-testid={`tier-${t}`}>{(t || "insufficient").replace("_", " ")}</span>;

export const TierLegend = ({ tiers }) => (
  <div className="rounded-xl border border-slate-800 p-3 space-y-1.5 text-[11px] text-slate-400 max-w-sm" data-testid="tier-legend">
    {Object.entries(tiers).map(([k, v]) => <div key={k} className="flex gap-2 items-start"><Tier t={k} /><span className="leading-snug">{v}</span></div>)}
  </div>
);

const Row = ({ label, m, thresholds }) => {
  if (!m) return null;
  const n = m.graded ?? 0;
  return (
    <div className="rounded-lg border border-slate-800/80 p-2.5 space-y-1" data-testid={`ev-row-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between gap-2"><span className="text-xs font-medium text-slate-200">{label}</span><span className="text-[10px] text-slate-500">n graded {n}{m.directional != null ? ` · calls ${m.directional}` : ""}</span></div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
        <span className="text-slate-500">Accuracy</span><span className="text-slate-200 font-mono-ticker">{m.accuracy_pct != null ? `${m.accuracy_pct}% (CI ${ci(m.ci95)})` : `not reported (< ${thresholds.report})`}</span>
        <span className="text-slate-500">Avg net / call</span><span className={`font-mono-ticker ${m.avg_net_pct > 0 ? "text-emerald-300" : m.avg_net_pct < 0 ? "text-rose-300" : "text-slate-300"}`}>{signed(m.avg_net_pct)}{m.avg_net_ci95 ? <span className="text-slate-500"> (CI {ci(m.avg_net_ci95)})</span> : null}</span>
        {m.sac_pct != null && <><span className="text-slate-500">Calls beating costs</span><span className="text-slate-200 font-mono-ticker">{m.sac_pct}%</span></>}
        {m.max_drawdown_pct != null && <><span className="text-slate-500">Max drawdown</span><span className="text-rose-200 font-mono-ticker">−{Math.abs(m.max_drawdown_pct)}%</span></>}
        {m.profit_factor != null && <><span className="text-slate-500">Profit factor</span><span className="text-slate-200 font-mono-ticker">{m.profit_factor}</span></>}
      </div>
    </div>
  );
};

export const EvidenceColumn = ({ tone, icon: Icon, title, tid, lead, horizons, labels, flat = false, extra }) => {
  const thresholds = { report: 30 };
  const entries = flat ? Object.entries(horizons?.all || {}) : null;
  return (
    <section className={`rounded-2xl border p-4 md:p-5 space-y-3 ${TONE[tone]}`} data-testid={tid}>
      <div className="flex items-center gap-2 font-display font-semibold text-base text-slate-100"><Icon className="w-5 h-5" /> {title}</div>
      <p className="text-xs text-slate-400 leading-relaxed">{lead}</p>
      {flat ? (
        entries.length ? entries.map(([hz, m]) => <Row key={hz} label={labels[hz] || hz} m={m} thresholds={thresholds} />) : <div className="text-xs text-slate-500" data-testid={`${tid}-empty`}>No resolved calls yet.</div>
      ) : horizons ? (
        ["7d", "24h"].map((hz) => (
          <div key={hz} className="space-y-1.5">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{hz === "7d" ? "7-day horizon" : "24-hour horizon"}</div>
            {Object.entries(labels).map(([k, l]) => <Row key={k} label={l} m={horizons[hz]?.[k]} thresholds={thresholds} />)}
          </div>
        ))
      ) : null}
      {extra}
    </section>
  );
};

export const EvidenceLedgerTable = ({ samples, thresholds }) => (
  <div className="tbl-scroll overflow-x-auto -mx-2 px-2" data-testid="evidence-table">
    <table className="w-full text-sm">
      <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800">{["Sample", "Type", "Graded", "Accuracy (95% CI)", "Avg net after costs", "Buy & hold, same windows", "Worst call", "Tier"].map((h) => <th key={h} className="py-2 pr-3 font-medium whitespace-nowrap">{h}</th>)}</tr></thead>
      <tbody>
        {(samples || []).map((s) => (
          <tr key={s.label} className="border-b border-slate-800/60" data-testid="evidence-sample-row">
            <td className="py-2 pr-3 text-slate-200">{s.label}</td>
            <td className="py-2 pr-3 text-xs text-slate-400">{s.kind === "live" ? "live ledger" : "paper / shadow"}</td>
            <td className="py-2 pr-3 font-mono-ticker">{s.graded}{s.next_threshold ? <span className="text-slate-500 text-[10px]"> / {s.next_threshold}</span> : null}</td>
            <td className="py-2 pr-3 font-mono-ticker whitespace-nowrap">{s.accuracy_pct != null ? `${s.accuracy_pct}% (${ci(s.accuracy_ci95)})` : <span className="text-slate-500">not reported (&lt; {thresholds.report})</span>}</td>
            <td className="py-2 pr-3 font-mono-ticker whitespace-nowrap">{s.avg_net_pct != null ? `${signed(s.avg_net_pct)} (${ci(s.avg_net_ci95)})` : "—"}</td>
            <td className="py-2 pr-3 font-mono-ticker whitespace-nowrap">{s.bh_avg_net_pct != null ? signed(s.bh_avg_net_pct) : "—"}</td>
            <td className="py-2 pr-3 font-mono-ticker text-rose-200">{s.worst_call_pct != null ? `${s.worst_call_pct}%` : "—"}</td>
            <td className="py-2 pr-3"><Tier t={s.tier_accuracy} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const Disclosures = ({ items }) => (
  <section className="apx-card p-5 md:p-6 space-y-3 border-rose-900/40" data-testid="transparency-disclosures">
    <h2 className="font-display font-semibold text-base md:text-lg flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-rose-300" /> Risk disclosures</h2>
    <ul className="list-disc list-inside space-y-2 text-sm text-slate-300 leading-relaxed">{items.map((m) => <li key={m}>{m}</li>)}</ul>
  </section>
);
